import { motion } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";
import { useLang } from "@/lib/i18n";

export function Capabilities() {
  const { t } = useLang();
  const FLOW = [1, 2, 3, 4].map((n) => ({ n: `0${n}`, name: t(`cap.${n}.name`), desc: t(`cap.${n}.desc`) }));
  return (
    <section data-testid="capabilities-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="08" label={t("cap.chapter")} testid="capabilities-chapter" />
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08] max-w-3xl">
            {t("cap.h2a")} <span className="italic text-gold-light">{t("cap.h2b")}</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {FLOW.map((f, i) => (
            <motion.div
              key={f.n}
              data-testid={`capability-${f.n}`}
              className="relative bg-ink p-8 lg:p-10 group hover:bg-surface transition-colors duration-500"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
            >
              <span className="font-serif text-6xl text-white/[0.06] absolute top-4 right-6 group-hover:text-gold/10 transition-colors duration-500">{f.n}</span>
              <span className="font-mono text-[11px] tracking-[0.3em] text-gold">{f.n}</span>
              <h3 className="font-serif text-2xl text-ivory mt-4">{f.name}</h3>
              <p className="mt-3 text-sm text-white/50 leading-relaxed">{f.desc}</p>
              {i < 3 && (
                <span className="hidden lg:block absolute top-1/2 -right-[7px] w-3.5 h-px bg-gold/60 z-10" />
              )}
            </motion.div>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-white/30">{t("cap.note")}</p>
        </Reveal>
      </div>
    </section>
  );
}

export function Methodology() {
  const { t } = useLang();
  const STEPS = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ n: `0${n}`, name: t(`method.${n}`), desc: t(`method.${n}.d`) }));
  const flow = t("method.flow").split("|");
  return (
    <section data-testid="methodology-section" className="py-28 lg:py-40 bg-surface/40 border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="09" label={t("method.chapter")} testid="methodology-chapter" />
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
            {t("method.h2a")} <span className="italic text-gold-light">{t("method.h2b")}</span>
          </h2>
        </Reveal>

        <div className="mt-16 relative">
          <motion.div
            className="hidden lg:block absolute top-[7px] left-0 right-0 h-px bg-white/10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ originX: 0 }}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-10 lg:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                data-testid={`method-step-${s.n}`}
                className="relative group"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <motion.span
                  className="block w-[15px] h-[15px] rounded-full border-2 border-gold bg-ink mb-6 group-hover:bg-gold transition-colors duration-500"
                  whileInView={{ boxShadow: "0 0 16px rgba(212,175,55,0.5)" }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 + 0.3 }}
                />
                <span className="font-mono text-[11px] tracking-[0.3em] text-gold">{s.n}</span>
                <h3 className="font-serif text-xl text-ivory mt-2 uppercase tracking-wide">{s.name}</h3>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Reveal delay={0.3} className="mt-16">
          <p data-testid="method-flow" className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/40 overflow-x-auto whitespace-nowrap pb-2">
            {flow.map((f, i) => (
              <span key={f}>
                {i > 0 && <span className="text-gold mx-2">→</span>}
                {f}
              </span>
            ))}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function AfricaEurope() {
  const { t, list } = useLang();
  return (
    <section data-testid="africa-europe-section" className="py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
        <svg viewBox="0 0 800 400" className="w-[900px]">
          {Array.from({ length: 20 }).map((_, i) => (
            <circle key={i} cx={60 + (i % 10) * 75} cy={60 + Math.floor(i / 10) * 260} r="1.5" fill="#F8F9FA" />
          ))}
          <path d="M120 120 Q 400 40 680 130" stroke="#D4AF37" strokeWidth="1" fill="none" strokeDasharray="4 6" />
          <path d="M680 280 Q 400 360 120 270" stroke="#D4AF37" strokeWidth="1" fill="none" strokeDasharray="4 6" />
        </svg>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
        <Chapter index="10" label={t("ae.chapter")} testid="africa-europe-chapter" />
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
            {t("ae.h2a")} <span className="italic text-gold-light">{t("ae.h2b")}</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[1fr_auto_1fr] gap-10 items-center">
          <Reveal className="border border-white/10 bg-ink/70 backdrop-blur-sm p-8 lg:p-10">
            <h3 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{t("ae.left")}</h3>
            <ul className="space-y-3">
              {list("ae.left.i").map((i) => (
                <li key={i} className="text-white/60 text-base border-b border-white/5 pb-3 last:border-0">{i}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="flex lg:flex-col items-center justify-center gap-4">
            <motion.span
              className="hidden lg:block w-px h-10 bg-gradient-to-b from-transparent to-gold/70"
              initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
            />
            <div data-testid="bsg-bridge" className="border border-gold/50 bg-gold/[0.06] px-10 py-6 text-center">
              <span className="font-serif text-2xl text-ivory">BSGOTECH</span>
              <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-gold mt-1.5">{t("ae.bridge")}</span>
            </div>
            <motion.span
              className="hidden lg:block w-px h-10 bg-gradient-to-b from-gold/70 to-transparent"
              initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
            />
          </Reveal>

          <Reveal delay={0.25} className="border border-white/10 bg-ink/70 backdrop-blur-sm p-8 lg:p-10">
            <h3 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{t("ae.right")}</h3>
            <ul className="space-y-3">
              {list("ae.right.i").map((i) => (
                <li key={i} className="text-white/60 text-base border-b border-white/5 pb-3 last:border-0">{i}</li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.3}>
          <p className="mt-14 font-serif italic text-2xl text-white/50 max-w-2xl leading-snug">
            “{t("ae.quote")}”
          </p>
        </Reveal>
      </div>
    </section>
  );
}
